import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui';
import { useAuth } from './AuthProvider';

/**
 * Connecting Google Drive and Gmail.
 *
 * Two reasons this is an explicit action rather than part of sign-in, and the
 * second one arrived later:
 *
 * The original one: a refresh token is the only durable credential Google
 * will give, it is issued only on a fresh consent, and asking for consent on
 * every sign-in would mean seeing the permissions screen forever.
 *
 * The one from 12 September: sign-in stopped asking for Drive and Gmail
 * permissions at all. Alex opened the CEO Brief and was met with a screen
 * saying an unverified app named after a Supabase project ref wanted to send
 * email as him — to read a page. Everyone was being asked, to serve the three
 * people who actually send. So this is now the ONLY place those permissions
 * are requested, and it is reached by the people who need them, at the moment
 * they need them.
 *
 * Which means this button is no longer only a repair. For somebody who has
 * never connected, it is the first grant.
 */

export const driveConnectionKey = ['drive-connection'] as const;

/** Whether this person has a stored refresh token. Never the token itself. */
export function useDriveConnection() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: driveConnectionKey,
    queryFn: async () => {
      const { data, error } = await client.rpc('has_google_refresh_token');
      if (error) {
        throw new Error(error.message);
      }
      return data === true;
    },
    enabled: access.status === 'approved',
    staleTime: 60_000,
    retry: false,
  });
}

export function ReconnectDriveButton({
  size = 'sm',
  label = 'Reconnect Google Drive',
}: {
  size?: 'sm' | 'md';
  label?: string;
}) {
  const { reconnectDrive } = useAuth();
  return (
    <Button variant="secondary" size={size} onClick={() => void reconnectDrive()}>
      {label}
    </Button>
  );
}

/**
 * The inline form, for the moment an upload has just failed. It says what to
 * do rather than what went wrong, because by the time someone reads this they
 * already know something went wrong.
 */
export function DriveReconnectNotice({ message }: { message?: string }) {
  return (
    <div className="drive-reconnect-notice">
      <span>{message ?? 'Google Drive needs reconnecting before this will work.'}</span>
      <ReconnectDriveButton />
    </div>
  );
}
